import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePageSEO } from '../hooks/usePageSEO';
import { breadcrumbSchema, faqSchema } from '../utils/structuredData';
import { getAppSupportBySlug } from '../utils/appsData';
import { ArrowRightIcon, LifeBuoyIcon, MailIcon, BookOpenIcon, ExternalLinkIcon, ChevronDownIcon, MessageSquareIcon } from 'lucide-react';
export function AppSupportPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const app = getAppSupportBySlug(slug);
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  usePageSEO({
    title: app ? `${app.name} Support | Design Flow Digitals` : 'App Support | Design Flow Digitals',
    description: app ? `Get help, setup guides, and troubleshooting for ${app.name}.` : 'Support center for Design Flow Digitals Shopify apps.',
    canonical: `/support/${slug}`,
    keywords: app ? `${app.name} support, ${app.name} help, Shopify app support, Design Flow Digitals support` : 'Shopify app support',
    jsonLd: app ? [breadcrumbSchema([{
      name: 'Home',
      path: '/'
    }, {
      name: 'Support',
      path: '/support'
    }, {
      name: `${app.name} Support`,
      path: `/support/${slug}`
    }]), ...(app.troubleshooting && app.troubleshooting.length > 0 ? [faqSchema(app.troubleshooting.map(f => ({
      question: f.q,
      answer: f.a
    })))] : [])] : undefined
  });
  if (!app) {
    return <div className="pt-40 pb-32 px-6 text-center" data-id="element-853" data-mp="bjn31gnltq0a">
        <h1 className="text-4xl font-bold mb-4" data-id="element-854" data-mp="bk55iv0v10gn">Support page not found</h1>
        <p className="text-gray-400 mb-8" data-id="element-855" data-mp="7ita28vb1l0j">
          The app support page you’re looking for doesn’t exist.
        </p>
        <Link to="/support" className="inline-flex items-center gap-2 bg-gradient-gold text-black px-8 py-4 rounded-full font-bold gold-glow-hover transition-all hover:scale-105" data-id="element-856" data-mp="26o33q2dxwieq">
          View Support Center <ArrowRightIcon className="w-5 h-5" data-id="element-857" data-mp="2gnbxov8t1x13" />
        </Link>
      </div>;
  }
  const Icon = app.icon;
  return <div className="w-full" data-id="element-858" data-mp="2plnkem6jwao9">
      {/* Hero */}
      <section className="relative pt-32 pb-16 overflow-hidden border-b border-white/5" data-id="element-859" data-mp="2pkw0w9q8a3ec">
        <div className="absolute inset-0 bg-grid-pattern opacity-5 pointer-events-none" data-id="element-860" data-mp="2ujp67evd2o3f" />
        <div className={`absolute top-0 right-1/4 w-96 h-96 ${app.accentBg} rounded-full blur-[120px] pointer-events-none`} data-id="element-861" data-mp="2zb7762xdwooe" />

        <div className="max-w-6xl mx-auto px-6 md:px-12 relative z-10" data-id="element-862" data-mp="34cmobjwfyb1i">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 mb-6" data-id="element-863" data-mp="39ay2u7xol9q2">
            <div className={`w-16 h-16 rounded-2xl glass-card flex items-center justify-center shrink-0 border ${app.accentBorder} ${app.accentBg}`} data-id="element-864" data-mp="3dbrmuq8064do">
              <Icon className={`w-8 h-8 ${app.accentColor}`} data-id="element-865" data-mp="xl89woqy1lcc" />
            </div>
            <div data-id="element-866" data-mp="smom0isshzhc">
              <h1 className="text-4xl md:text-5xl font-bold mb-2" data-id="element-867" data-mp="so9iidynzshu">
                {app.name} Support
              </h1>
              <p className={`text-lg ${app.accentColor} font-medium`} data-id="element-868" data-mp="npoiofnbratx">
                {app.tagline}
              </p>
            </div>
          </div>
          <p className="text-xl text-gray-400 leading-relaxed max-w-2xl" data-id="element-869" data-mp="1gd160s1b7moa">
            {app.description}
          </p>
        </div>
      </section>

      {/* Quick Contact Band */}
      <section className="py-8 border-b border-white/5 bg-white/[0.02]" data-id="element-870" data-mp="1bjpx8lcxsvtq">
        <div className="max-w-6xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left" data-id="element-871" data-mp="16mpo21ksjcye">
          <div className="flex flex-col sm:flex-row items-center gap-4" data-id="element-872" data-mp="1zbwtzlmkr4to">
            <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center shrink-0" data-id="element-873" data-mp="1z9tvseb9q5dv">
              <MailIcon className="w-5 h-5 text-gray-300" data-id="element-874" data-mp="1nv8rk8azlo7v" />
            </div>
            <div data-id="element-875" data-mp="1nt4eazr77cux">
              <p className="text-white font-medium" data-id="element-876" data-mp="19vft1qt7y3pc">Need direct help?</p>
              <p className="text-sm text-gray-400" data-id="element-877" data-mp="19xadz54hpvtf">
                Email{' '}
                <a href={`mailto:${app.contact.email}`} className="text-white hover:underline" data-id="element-878" data-mp="1evbzrxtaw98l">
                  {app.contact.email}
                </a>
              </p>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-879" data-mp="27lary22skmhh">
                Replies usually {app.contact.responseTime}
              </p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-4 w-full md:w-auto" data-id="element-880" data-mp="1xsd7hqe8ong8">
            {app.shopifyUrl && <a href={app.shopifyUrl} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto inline-flex justify-center items-center gap-2 px-5 py-2.5 rounded-full glass-card text-sm font-medium hover:bg-white/5 transition-colors" data-id="element-881" data-mp="2lhoalej2atxj">
                Open App Store <ExternalLinkIcon className="w-4 h-4" data-id="element-882" data-mp="nnk5hz6tnjan" />
              </a>}
            <Link to={app.productRoute} className="w-full sm:w-auto inline-flex justify-center items-center gap-2 px-5 py-2.5 rounded-full glass-card text-sm font-medium hover:bg-white/5 transition-colors" data-id="element-883" data-mp="jlfc91kacooj">
              View Product Page <ArrowRightIcon className="w-4 h-4" data-id="element-884" data-mp="3n4091bdtlo28" />
            </Link>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20" data-id="element-885" data-mp="3w33tojsmybiu">
        <div className="max-w-6xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-3 gap-16" data-id="element-886" data-mp="3w22jehjg1t26">
          {/* Left Column: Getting Started */}
          <div className="lg:col-span-2 space-y-16" data-id="element-887" data-mp="1gdiww18hnsih">
            <div data-id="element-888" data-mp="1bbtnsr4ttox1">
              <div className="flex items-center gap-3 mb-8" data-id="element-889" data-mp="1bcd6wb67qjcn">
                <BookOpenIcon className={`w-6 h-6 ${app.accentColor}`} data-id="element-890" data-mp="9003dmvpwpfa" />
                <h2 className="text-3xl font-bold" data-id="element-891" data-mp="dy1n8a0ywsrt">Getting Started</h2>
              </div>
              <div className="space-y-8 relative before:absolute before:inset-0 before:ml-6 before:-translate-x-px before:h-full before:w-0.5 before:bg-gradient-to-b before:from-white/10 before:via-white/10 before:to-transparent" data-id="element-892" data-mp="hzvke8byfen8">
                {app.gettingStarted.map((step, i) => <div key={i} className="relative flex items-start gap-6" data-id="element-893" data-mp="3mca80vx90f9f" data-mpi={i}>
                    <div className={`w-12 h-12 rounded-full glass-card flex items-center justify-center shrink-0 border ${app.accentBorder} ${app.accentBg} relative z-10`} data-id="element-894" data-mp="3qfyh5j1j3iws">
                      <span className={`font-bold ${app.accentColor}`} data-id="element-895" data-mp="426wssqi5teh">
                        {i + 1}
                      </span>
                    </div>
                    <div className="pt-2" data-id="element-896" data-mp="37o5ppt23y5r6">
                      <h3 className="text-xl font-bold text-white mb-2" data-id="element-897" data-mp="37mujlml6wlww">
                        {step.title}
                      </h3>
                      <p className="text-gray-400 leading-relaxed" data-id="element-898" data-mp="2v6bp2pwbuxpt">
                        {step.desc}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>

            {/* Troubleshooting / FAQ */}
            <div data-id="element-899" data-mp="2h6tvzyc00yan">
              <div className="flex items-center gap-3 mb-8" data-id="element-900" data-mp="2h7c2rowaeuwt">
                <LifeBuoyIcon className={`w-6 h-6 ${app.accentColor}`} data-id="element-901" data-mp="2h8wruyzu1lna" />
                <h2 className="text-3xl font-bold" data-id="element-902" data-mp="2ceiwuxp31gj5">Troubleshooting & FAQ</h2>
              </div>
              <div className="space-y-4" data-id="element-903" data-mp="27hk34g9bzblh">
                {app.troubleshooting.map((faq, i) => <div key={i} className={`glass-card rounded-2xl overflow-hidden transition-all duration-300 ${openFaq === i ? `border ${app.accentBorder}` : 'border-white/5 hover:border-white/10'}`} data-id="element-904" data-mp="22jil7ynn5ibl" data-mpi={i}>
                    <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full px-6 py-5 flex items-center justify-between text-left" data-id="element-905" data-mp="3hdfk87npmkc4">
                      <span className="font-semibold text-lg pr-8" data-id="element-906" data-mp="38c7n3swxjpjm">
                        {faq.q}
                      </span>
                      <ChevronDownIcon className={`w-5 h-5 shrink-0 transition-transform duration-300 ${openFaq === i ? 'rotate-180 text-white' : 'text-gray-500'}`} data-id="element-907" data-mp="3cd1b0madh4ci" />
                    </button>
                    <div className={`overflow-hidden transition-all duration-300 ${openFaq === i ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-908" data-mp="2opjlkxekyicp">
                      <p className="px-6 pb-6 text-gray-400 leading-relaxed" data-id="element-909" data-mp="2fotvmac03m6w">
                        {faq.a}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>

          {/* Right Column: Sticky Sidebar */}
          <div className="lg:col-span-1" data-id="element-910" data-mp="1w31qomxux0s0">
            <div className="sticky top-32 glass-card p-8 rounded-3xl border-white/10" data-id="element-911" data-mp="1w43dfoo8jbpk">
              <div className={`w-12 h-12 rounded-xl mb-6 flex items-center justify-center ${app.accentBg} border ${app.accentBorder}`} data-id="element-912" data-mp="2a3m93hzv0ukz">
                <MessageSquareIcon className={`w-6 h-6 ${app.accentColor}`} data-id="element-913" data-mp="2k0haq9rfjb3h" />
              </div>
              <h3 className="text-xl font-bold mb-3" data-id="element-914" data-mp="2f1xk3w1w5b4s">Still need help?</h3>
              <p className="text-gray-400 text-sm mb-6 leading-relaxed" data-id="element-915" data-mp="2ezjp1n77nyx8">
                Can't find the answer you're looking for? Our support team is
                ready to help you get the most out of {app.name}.
              </p>
              <Link to="/contact" className="w-full flex items-center justify-center gap-2 bg-white text-black px-6 py-3 rounded-full font-semibold hover:bg-gray-200 transition-colors" data-id="element-916" data-mp="32qp8sdg9vbct">
                Contact Support
              </Link>
              <div className="mt-6 pt-6 border-t border-white/10" data-id="element-917" data-mp="3e9jso4onk0l0">
                <p className="text-xs text-gray-500 text-center" data-id="element-918" data-mp="3e771d0x08dva">
                  Support hours: Mon-Fri, 9am-5pm EST
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>;
}